// Ported from packages/workflow-engine-upstream/ui/src/PlanPanel.tsx at commit bebc382. Changed from the
// reference: selecting a step also names the version it was selected in, for the inspector.
// Column 2: the plan's version tabs, each step's live status, Approve, Edit and Compare. Selecting
// a step opens the inspector. Edit opens the plan's steps as JSON; saving sends them to the engine,
// which compiles them against the Context and keeps them as the next version (F7), or names what
// does not compile. Compare shows what a version changed against the one it came from (F8).
import { useState } from 'react';
import type { Plan } from './contracts';
import type { PickedStep } from './AnswerPanel';
import type { RunState } from './api';
import { comparePlans, stepMark, versionLabel } from './compare';
import { PlanCard, planCardOf } from './PlanCard';
import { stepKind, stepLabel } from './step-label';

/** A step's status: its trace outcome once it has one, "running" once started, else "pending". */
export function stepStatus(s: RunState, stepId: string): string {
  const done = s.trace.steps.find((x) => x.stepId === stepId)?.outcome;
  if (done) return done;
  return s.events.some((e) => e.type === 'step_started' && e.stepId === stepId) ? 'running' : 'pending';
}

const ICON_CLASS: Record<string, string> = { ok: 'done', done: 'done', empty: 'done', parked: 'parked', running: 'running' };

function noPlan(s: RunState): string {
  if (s.status === 'finished') return 'No plan was needed: the run answered at once.';
  if (s.status === 'awaiting_ask') return 'The plan comes once the ask is answered.';
  return 'No plan yet.';
}

function PlanEditor({ plan, onSave, onClose }: { plan: Plan; onSave: (plan: Plan) => Promise<void>; onClose: () => void }) {
  const [text, setText] = useState(() => JSON.stringify(plan.steps, null, 2));
  const [error, setError] = useState('');
  const save = () => {
    let steps: Plan['steps'];
    try {
      steps = JSON.parse(text) as Plan['steps'];
    } catch (e) {
      setError(`The steps are not valid JSON: ${(e as Error).message}`);
      return;
    }
    setError('');
    onSave({ ...plan, steps, reason: 'edit' }).then(onClose, (e: Error) => setError(e.message));
  };
  return (
    <div className="editor">
      <span className="note">Edit the steps: change, reorder or remove them. Saving makes v{plan.version + 1}, which needs its own approval.</span>
      <textarea value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} />
      {error && <div className="err">{error}</div>}
      <div className="btns">
        <button type="button" className="p" onClick={save}>
          Save as v{plan.version + 1}
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function Compare({ before, after }: { before: Plan; after: Plan }) {
  const d = comparePlans(before, after);
  const none = !d.added.length && !d.removed.length && !d.changed.length;
  return (
    <div className="compare">
      <b>
        v{d.to} against v{d.from}
      </b>
      {none && <span className="note">The steps are the same.</span>}
      {d.added.map((s) => (
        <div key={`a${s.id}`} className="diff add">
          + {s.id} · {s.title}
        </div>
      ))}
      {d.removed.map((s) => (
        <div key={`r${s.id}`} className="diff del">
          − {s.id} · {s.title}
        </div>
      ))}
      {d.changed.map((c) => (
        <div key={`c${c.id}`} className="diff chg">
          ~ {c.id} · {c.title} <span className="note">({c.fields.join(', ')})</span>
        </div>
      ))}
      {d.kept.length > 0 && <span className="note">Kept as they were: {d.kept.join(', ')}.</span>}
    </div>
  );
}

export function PlanPanel({ s, selected, onSelect, onApprove, onEdit }: { s: RunState; selected: PickedStep | null; onSelect: (step: PickedStep) => void; onApprove: () => void; onEdit: (plan: Plan) => Promise<void> }) {
  const versions = s.trace.planVersions;
  const latest = versions.at(-1);
  const [picked, setPicked] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const plan = versions.find((p) => p.version === picked) ?? latest;
  const isLatest = plan === latest;
  const parent = plan && versions.find((p) => p.version === (plan.parentVersion ?? plan.version - 1) && p.version !== plan.version);
  const waiting = s.status === 'awaiting_approval' && isLatest;
  const card = plan ? planCardOf(s.events, plan.version) : null;
  return (
    <div className="col">
      <span className="h">2 · Plan</span>
      {!plan ? (
        <span className="note">{noPlan(s)}</span>
      ) : (
        <>
          <div className="tabs">
            {versions.map((p) => {
              const label = versionLabel(p, s.events);
              return (
                <button key={p.version} type="button" title={p.reason ?? ''} className={p.version === plan.version ? 'chip on' : 'chip'} onClick={() => setPicked(p === latest ? null : p.version)}>
                  v{p.version}
                  {label ? ` · ${label}` : ''}
                </button>
              );
            })}
            <span className="note">{card ? 'worked out from the data, part by part' : plan.source.kind === 'catalog' ? `from ${plan.source.procedureId} (catalog)` : plan.source.kind === 'file' ? `from supplied file (${plan.source.documentId})` : plan.source.kind === 'interview' ? `from interview (${plan.source.documentId})` : plan.source.kind === 'question' ? 'from your steps' : 'worked out from the data'}</span>
          </div>
          {!isLatest && <span className="note">An earlier version, for reading. The run follows v{latest!.version}.</span>}
          {plan.steps.map((st, i) => {
            const status = isLatest ? stepStatus(s, st.id) : '';
            const cls = ICON_CLASS[status] ?? '';
            const mark = stepMark(versions, plan, st.id);
            // A later part of a method step shows only its short line: the method text is on its first part.
            const label = stepLabel(plan, st);
            return (
              <button key={st.id} type="button" className={`step ${cls} ${selected?.id === st.id && selected.version === plan.version ? 'sel' : ''} ${label.heading ? '' : 'part'}`} onClick={() => onSelect({ id: st.id, version: plan.version })}>
                <i>{cls === 'done' ? '✓' : i + 1}</i>
                <span>
                  {label.heading}
                  {label.heading && label.part && <br />}
                  {label.part && <span className="part-line">{label.part}</span>}
                  <br />
                  <span className="note">
                    {st.id} · {stepKind(st)}
                    {st.sourceStep ? ` · ${st.sourceStep}` : ''}
                  </span>
                </span>
                <span className="st">
                  {mark && <span className="chip def">{mark} in v{plan.version}</span>} {status}
                </span>
              </button>
            );
          })}
          {card && <PlanCard card={card} />}
          <details className="plan-bindings">
            <summary>Review operations and bindings before approval</summary>
            {plan.steps.map((st) => (
              <div key={st.id}>
                <b>{st.id} · {st.title}</b>
                <pre>{JSON.stringify({ operation: st.op ?? null, bindings: st.bindings ?? {} }, null, 2)}</pre>
              </div>
            ))}
          </details>
          <details className="plan-bindings">
            <summary>Review requirements and method inputs</summary>
            {plan.requirements.map((requirement) => (
              <div key={requirement.id} className="requirement">
                <b>{requirement.id}</b>
                <div>{requirement.description}</div>
                <span className="note">Steps: {requirement.satisfiedBy?.join(', ') || 'No evidence step assigned'}</span>
              </div>
            ))}
          </details>
          <div className="btns">
            <button type="button" className="p" disabled={!waiting} onClick={onApprove}>
              Approve v{plan.version}
            </button>
            <button type="button" disabled={!waiting} onClick={() => setEditing(true)}>
              Edit
            </button>
            <button type="button" disabled={!parent} title={parent ? '' : 'v1 has no earlier version to compare with'} onClick={() => setComparing(!comparing)}>
              {parent ? `Compare v${parent.version}` : 'Compare'}
            </button>
          </div>
          {comparing && parent && <Compare before={parent} after={plan} />}
          {editing && waiting && <PlanEditor key={plan.version} plan={plan} onSave={onEdit} onClose={() => setEditing(false)} />}
          <span className="note">Live status comes from the run's events. Select a step to inspect it.</span>
        </>
      )}
    </div>
  );
}
